import type { StoreApi } from 'zustand'
import type { createAuthApiClient } from '../infrastructure/authApiClient'
import type {
  AuthForm,
  AuthStore,
  CreateAccountDetails,
  LoginCredentials,
  LoginError,
  ResetPasswordDetails,
} from './types'
import type { MyPortalNotification } from '@/scopes/common/domain/myPortalNotification'
import type { Logger } from '@/scopes/common/domain/types'
import type { ConfigurationClient } from '@/scopes/common/domain/types/configuration'
import type { BrowserNavigationEvents } from '@/scopes/common/infrastructure/dom/browserNavigationEvents'
import type { LocationResolver } from '@/scopes/common/infrastructure/dom/location'
import type { QueryStringClient } from '@/scopes/common/infrastructure/query-strings/queryStringClient'
import type { createUserAuthenticationClient } from '@/scopes/common/infrastructure/user'
import { toError } from '@/scopes/common/domain/functions/toError'
import { MY_PORTAL_NOTIFICATION_QUERY_PARAM } from '@/scopes/common/domain/myPortalNotification'
import { getAuthFormQueryValue, parseAuthForm } from './authForm'
import { getReturnUrl } from './getReturnUrl'
import { LoginRequestError } from './LoginRequestError'

const AUTO_LOGIN_TOKEN_QUERY_PARAM = 'asit'

interface AuthActionDependencies {
  apiClient: ReturnType<typeof createAuthApiClient>
  store: StoreApi<AuthStore>
  configurationClient: ConfigurationClient
  location: LocationResolver
  queryStringClient: QueryStringClient
  userAuthenticationClient: ReturnType<typeof createUserAuthenticationClient>
  logger: Logger
  scrollToTop: () => void
  focusFormTitle: () => void
  browserNavigationEvents: BrowserNavigationEvents
}

export function getPostAuthRedirectUrl(
  configurationClient: ConfigurationClient,
  location: LocationResolver,
  myPortalNotification?: MyPortalNotification,
) {
  const returnUrl = getReturnUrl(location.search(), configurationClient.getField('domainUrl'))
  if (returnUrl)
    return returnUrl

  const myPortalUrl = configurationClient.getField('links.myPortal')
  return myPortalNotification
    ? `${myPortalUrl}?${new URLSearchParams({ [MY_PORTAL_NOTIFICATION_QUERY_PARAM]: myPortalNotification })}`
    : myPortalUrl
}

function getLoginPageRedirectUrl(configurationClient: ConfigurationClient, location: LocationResolver) {
  return getReturnUrl(location.search(), configurationClient.getField('domainUrl')) || '/'
}

export function createAuthActions({
  apiClient,
  store,
  configurationClient,
  location,
  queryStringClient,
  userAuthenticationClient,
  logger,
  scrollToTop,
  focusFormTitle,
  browserNavigationEvents,
}: AuthActionDependencies) {
  function toLoginError(error: unknown): LoginError {
    return error instanceof LoginRequestError ? error.loginError : { type: 'unexpectedError' }
  }

  function canSubmit(form: AuthForm) {
    const state = store.getState()

    return state.submitStatus !== 'loading' && state.pageInitializationStatus === 'success' && state.activeForm === form
  }

  function showSubmitError(error: unknown, action: string) {
    if (!(error instanceof LoginRequestError))
      logger.logError(toError(error), { action })

    store.getState().setIn('submitError', toLoginError(error))
    store.getState().setIn('submitStatus', 'error')
  }

  function normalizeAuthFormQuery() {
    const search = queryStringClient.get()
    const viewValues = new URLSearchParams(search).getAll('view')
    const canonicalView = getAuthFormQueryValue(parseAuthForm(search))

    if (viewValues.length !== 1 || viewValues[0] !== canonicalView)
      queryStringClient.replaceValue('view', canonicalView)
  }

  function takeAutoLoginTokenFromUrl() {
    const token = queryStringClient.getValue(AUTO_LOGIN_TOKEN_QUERY_PARAM)

    if (token)
      queryStringClient.remove(AUTO_LOGIN_TOKEN_QUERY_PARAM)

    return token
  }

  async function hasAutoLoggedIn(token: string) {
    store.getState().setIn('submitStatus', 'loading')

    try {
      await apiClient.autoLogin(token)
      location.replaceWith(getPostAuthRedirectUrl(configurationClient, location))
      return true
    }
    catch (error) {
      logger.logError(toError(error), { action: 'pageLoaded' })
      store.getState().setIn('submitStatus', 'idle')
      return false
    }
  }

  async function loginRequested(credentials: LoginCredentials) {
    const state = store.getState()

    if (!canSubmit('login'))
      return

    state.setIn('submitError', null)
    state.setIn('submitStatus', 'loading')

    try {
      await apiClient.login(credentials)
      location.replaceWith(getPostAuthRedirectUrl(configurationClient, location))
    }
    catch (error) {
      showSubmitError(error, 'loginRequested')
    }
  }

  async function pageLoaded() {
    normalizeAuthFormQuery()

    const state = store.getState()

    if (state.pageInitializationStatus !== 'idle')
      return

    browserNavigationEvents.onHistoryChanged(formChangedByHistory)
    browserNavigationEvents.onPageRestoredFromCache(pageRestoredFromCache)

    const autoLoginToken = takeAutoLoginTokenFromUrl()

    state.setIn('submitError', null)
    state.setIn('pageInitializationStatus', 'loading')

    try {
      const userInfo = await userAuthenticationClient.getUserInfo()
      if (userInfo.isUserLoggedIn) {
        location.replaceWith(getLoginPageRedirectUrl(configurationClient, location))
        return
      }

      if (autoLoginToken && await hasAutoLoggedIn(autoLoginToken))
        return

      store.getState().setIn('pageInitializationStatus', 'success')
    }
    catch (error) {
      logger.logError(toError(error), { action: 'pageLoaded' })
      store.getState().setIn('submitError', { type: 'unexpectedError' })
      store.getState().setIn('pageInitializationStatus', 'error')
    }
  }

  async function createAccountRequested(details: CreateAccountDetails) {
    const state = store.getState()

    if (!canSubmit('createAccount'))
      return

    state.setIn('submitError', null)
    state.setIn('submitStatus', 'loading')

    try {
      await apiClient.createAccount(details)
      store.getState().setIn('submitStatus', 'success')
      scrollToTop()
    }
    catch (error) {
      showSubmitError(error, 'createAccountRequested')
    }
  }

  function showForm(form: AuthForm) {
    const state = store.getState()

    state.setIn('submitStatus', 'idle')
    state.setIn('submitError', null)
    state.setIn('activeForm', form)
    scrollToTop()
    focusFormTitle()
  }

  function openForm(form: AuthForm) {
    if (store.getState().submitStatus === 'loading')
      return

    const params = new URLSearchParams(queryStringClient.get())
    params.set('view', getAuthFormQueryValue(form))

    if (store.getState().pageInitializationStatus === 'error') {
      location.redirectTo(`?${params}`)
      return
    }

    queryStringClient.set(params.toString())
    showForm(form)
  }

  function formChangedByHistory() {
    if (store.getState().submitStatus === 'loading')
      return

    if (store.getState().pageInitializationStatus === 'error') {
      location.replaceWith(location.href())
      return
    }

    showForm(parseAuthForm(queryStringClient.get()))
  }

  function pageRestoredFromCache() {
    location.replaceWith(location.href())
  }

  function createAccountFormOpened() {
    openForm('createAccount')
  }

  function createAccountConfirmationDismissed() {
    const state = store.getState()

    state.setIn('submitStatus', 'idle')
    state.setIn('submitError', null)
  }

  async function resetPasswordRequested(details: ResetPasswordDetails) {
    const state = store.getState()

    if (!canSubmit('resetPassword'))
      return

    state.setIn('submitError', null)
    state.setIn('submitStatus', 'loading')

    try {
      await apiClient.resetPassword(details)
      store.getState().setIn('submitStatus', 'success')
      scrollToTop()
    }
    catch (error) {
      showSubmitError(error, 'resetPasswordRequested')
    }
  }

  function resetPasswordFormOpened() {
    openForm('resetPassword')
  }

  function loginFormOpened() {
    openForm('login')
  }

  return {
    pageLoaded,
    loginRequested,
    createAccountRequested,
    createAccountFormOpened,
    createAccountConfirmationDismissed,
    resetPasswordRequested,
    resetPasswordFormOpened,
    loginFormOpened,
  }
}
